import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Buildings, CircleNotch, PaperPlaneTilt } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { EDITION_ID } from "@/lib/edition";

const EMPTY = {
  full_name: "", whatsapp: "", email: "", gender: "",
  institution_name: "", address: "", city_village: "", district: "", state: "", notes: "",
};

// Lightweight "Help us add your institution" lead form. Auto-creates the
// institution and captures the submitter's details for the admin CRM.
export default function AddInstitutionRequestDialog({ open, onOpenChange, prefillName = "" }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (open) setF((prev) => ({ ...EMPTY, ...prev, institution_name: prefillName || prev.institution_name }));
  }, [open, prefillName]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim()) return toast.error("Please enter your full name");
    if (!f.whatsapp.trim()) return toast.error("Please enter your WhatsApp number");
    if (!f.institution_name.trim()) return toast.error("Please enter the institution name");
    setBusy(true);
    try {
      const { data } = await api.post("/public/institution-requests", { ...f, edition: EDITION_ID });
      toast.success("Thank you! We've created your institution page — you can refine it anytime.");
      setF(EMPTY);
      onOpenChange(false);
      if (data.institution_id) nav(`/institutions/${data.institution_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit — please try again");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="add-institution-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground"><Buildings size={18} weight="fill" /></span>
            Can't find your institution?
          </DialogTitle>
          <DialogDescription>
            Tell us about it — school, college or one that no longer exists. We'll create its page and our team will verify the details.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Your full name *</label>
              <Input value={f.full_name} onChange={set("full_name")} placeholder="e.g. Anita Sharma" data-testid="req-full-name" className="rounded-xl" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">WhatsApp number *</label>
              <Input value={f.whatsapp} onChange={set("whatsapp")} placeholder="+91 98765 43210" data-testid="req-whatsapp" className="rounded-xl" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Email (optional)</label>
              <Input type="email" value={f.email} onChange={set("email")} placeholder="you@example.com" data-testid="req-email" className="rounded-xl" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Gender</label>
              <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
                <SelectTrigger data-testid="req-gender" className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                  <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Name of the institution *</label>
            <Input value={f.institution_name} onChange={set("institution_name")} placeholder="Existing or gone — as you remember it" data-testid="req-institution-name" className="rounded-xl" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Address</label>
            <Input value={f.address} onChange={set("address")} placeholder="Street / landmark" data-testid="req-address" className="rounded-xl" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">City / Village</label>
              <Input value={f.city_village} onChange={set("city_village")} placeholder="Panchayat / town" data-testid="req-city-village" className="rounded-xl" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">District</label>
              <Input value={f.district} onChange={set("district")} placeholder="District" data-testid="req-district" className="rounded-xl" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">State</label>
              <Input value={f.state} onChange={set("state")} placeholder="State" data-testid="req-state" className="rounded-xl" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Additional notes</label>
            <Textarea value={f.notes} onChange={set("notes")} placeholder="Anything that helps us identify it — former name, founding year, whether it merged or closed…" data-testid="req-notes" className="rounded-xl" rows={3} />
          </div>

          <Button type="submit" disabled={busy} className="w-full gap-1.5 rounded-full" data-testid="req-submit">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="bold" />}
            Submit & create the page
          </Button>
          <p className="text-center text-[11px] text-muted-foreground">We'll review and verify the details. Sign in to edit your institution and submit updates for approval.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
